import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";

const TIER_GROUPS = [
  { rank: 1, key: "award_winner",      label: "Award Winners",     accent: "from-amber-400 to-yellow-600" },
  { rank: 2, key: "finalist",          label: "Finalists",          accent: "from-zinc-200 to-zinc-400" },
  { rank: 3, key: "semi_finalist",     label: "Semi-Finalists",     accent: "from-amber-700 to-orange-700" },
  { rank: 4, key: "quarter_finalist",  label: "Quarter-Finalists",  accent: "from-amber-800 to-orange-800" },
  { rank: 5, key: "nominee",           label: "Nominees",           accent: "from-yellow-500 to-amber-500" },
  { rank: 6, key: "honorable_mention", label: "Honorable Mentions", accent: "from-amber-600 to-amber-500" },
  { rank: 7, key: "selected",          label: "Selected",            accent: "from-amber-500 to-yellow-500" },
  { rank: 8, key: "official_selection",label: "Official Selections", accent: "from-red-500 to-red-700" },
];

export default function LaurelsWall() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sp, setSp] = useSearchParams();
  const tierFilter = sp.get("tier") || "";

  useEffect(() => {
    api.get("/festival/laurels-wall")
      .then((r) => setData(r.data))
      .catch(() => setData({ count: 0, laurels: [] }))
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    const all = (data?.laurels || []);
    return TIER_GROUPS
      .map((g) => ({ ...g, films: all.filter((l) => l.badge_kind === g.key) }))
      .filter((g) => g.films.length > 0)
      .filter((g) => !tierFilter || g.key === tierFilter);
  }, [data, tierFilter]);

  const totalCount = groups.reduce((s, g) => s + g.films.length, 0);

  const copyTierLink = (tierKey) => {
    const url = `${window.location.origin}/festival/laurels?tier=${tierKey}`;
    navigator.clipboard.writeText(url);
    toast.success(`Deep link copied — share to invite people to this tier`);
  };

  const clearFilter = () => { sp.delete("tier"); setSp(sp, { replace: true }); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main className="pt-28 px-6 lg:px-12 max-w-[1400px] mx-auto pb-20">
        <header className="text-center mb-12">
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-3">World Film Festival</div>
          <h1 className="text-5xl lg:text-6xl font-black tracking-tight">Wall of Laurels</h1>
          <p className="text-gray-400 mt-3 max-w-2xl mx-auto">
            Every film honored at the festival — winners, finalists, and official selections — gathered in one place.
          </p>
          {tierFilter && (
            <div className="mt-5 inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5">
              <span className="text-xs uppercase tracking-widest text-amber-300">
                Showing only: {tierFilter.replace(/_/g, " ")}
              </span>
              <button
                onClick={clearFilter}
                data-testid="clear-tier-filter"
                className="text-amber-200 hover:text-white text-xs font-bold"
              >
                ✕ clear
              </button>
            </div>
          )}
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>
        ) : groups.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            No laurels yet. <Link to="/festival" className="text-amber-400 hover:underline">Browse the festival</Link>.
          </div>
        ) : (
          <div className="space-y-16">
            {groups.map((g) => (
              <section key={g.key} data-testid={`laurels-group-${g.key}`}>
                <div className="flex items-center gap-4 mb-6 flex-wrap">
                  <div className={`h-1 w-12 rounded-full bg-gradient-to-r ${g.accent}`} />
                  <h2 className="text-2xl font-black tracking-tight">{g.label}</h2>
                  <span className="text-xs uppercase tracking-widest text-gray-500">{g.films.length} {g.films.length === 1 ? "film" : "films"}</span>
                  <button
                    onClick={() => copyTierLink(g.key)}
                    data-testid={`share-tier-${g.key}`}
                    className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-amber-300 hover:text-white border border-amber-500/30 hover:border-amber-300 px-3 py-1 rounded-full transition-colors"
                  >
                    <Link2 className="w-3 h-3" /> Share this tier
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {g.films.map((f) => (
                    <Link
                      key={f.submission_id}
                      to={`/festival/film/${f.submission_id}`}
                      data-testid={`laurel-link-${f.submission_id}`}
                      className="group block bg-[#141414] border border-white/5 rounded-lg overflow-hidden hover:border-amber-500/40 transition-colors"
                    >
                      <img src={f.image_url} alt={f.title} loading="lazy"
                        className="w-full aspect-[2/3] object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="p-3">
                        <div className="text-sm font-bold truncate">{f.title}</div>
                        <div className="text-[11px] text-gray-500 truncate">
                          {f.director ? `dir. ${f.director}` : ""}{f.director && f.country ? " · " : ""}{f.country || ""}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
